
import { useLocation } from 'react-router'
import { useEffect, useState } from 'react'
import { ROLES } from '../../../config/user'
import { IUser } from '../../../types/users'
import UserAppBar from '../UserPanel/UserAppBar'
import EndServiceDialog from './EndServiceDialog'
import { IInput } from '../../../types/templates'
import { BlobProvider } from '@react-pdf/renderer'
import InputItem from './InputItem'
import DialogLink from '../../../components/DialogLink'
import { updateService } from '../../../services/services'
import { getUser, getUserId } from '../../../services/user'
import { useSession } from '../../../context/SessionContext'
import FilePreviewer from '../../../components/FilePreviewer'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import SuperUserAppBar from '../SuperUserPanel/SuperUserAppBar'
import { sendSignDocumentEmail } from '../../../services/emails'
import Alert, { ActionsOptType } from '../../../components/Alert'
import { TemplateGenerator } from './TemplateGenerator'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'
import { IService, IServiceData, SERVICE_STATUS } from '../../../types/services'
import { IInputValue, ITemplate, ITemplateData } from '../../../types/templates'
import { Button, Container, Grid, MobileStepper, Typography } from '@material-ui/core'

// Define IDialogEnd interface
const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  stepper: {
    background: 'transparent',
    paddingBottom: theme.spacing(4),
  },
}))

/**
 * Custom `TemplateEditor` component declaration.
 * This component is used to 
 * @return TemplateEditor
*/
const TemplateEditor = (): JSX.Element => {
  const classes = useStyles()
  const theme = useTheme()
  const { user } = useSession()
  //
  const location: any = useLocation() 
  const [watermark, setWatermark] = useState<any>(null) 
  const [templateState, setTemplateState] = useState<any>(null)  
  const [inputValuesData, setInputValuesData] = useState<any>([])
  //
  const loadData = ((): { template: ITemplate; service?: IService } => {
    if (location.state.origin)
      return {
        template: location.state.template as ITemplate,
        service: location.state.service as IService,
      }
    else return { template: location.state as ITemplate }
  })()
  const template = loadData.template
  let service = loadData.service
  const templateData = (template?.template_data || {
    blocks: [],
    inputs: [],
  }) as ITemplateData
  const [base64File, setBase64File] = useState<string>()
  const [inputValues, setInputValues] = useState<IInputValue[]>(
    service?.service_data ? service.service_data.inputValues : []
  )
  const [activeStep, setActiveStep] = useState(0)
  const [inputValue, setInputValue] = useState<IInputValue | undefined>(() => {
    const inputValue = inputValues.find((i: IInputValue) => i.pos === 0)
    return inputValue
  })
  const [openEndDialog, setOpenEndDialog] = useState(false)
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false)
  const [inputError, setInputError] = useState({ error: false, label: '' })
  const [firstDowload, setFirstDowload] = useState(
    service?.service_data ? service.service_data.first_download : false
  )
  const [userObj, setUserObj] = useState<IUser>()
  const [alertOptions, setAlertOptions] = useState<ActionsOptType>({
    message: null,
    type: null,
    open: false,
  })
  const [infoDataShareTo, setInfoDataShareTo] = useState<any[]>()
  const [currentChages, setCurrentChages] = useState<any[]>([])
  const [oldService, setOldService] = useState<IService>();
  const [loadedData, setLoadedData] = useState<boolean>(false)  
  const [inputSign, setInputSign] = useState<number>()
  const [userObjCreator, setUserObjCreator] = useState<IUser>()
  const [validatorConditional, setValidatorConditional] = useState(true)

  if(inputValuesData.length === 0)
  {
    setInputValuesData(service?.service_data?.inputValues)
  }
  if(!(templateState && templateState.id))
  {
    setTemplateState(template)
  }
  if(watermark === null)
  {
    setWatermark(service?.service_data ? service?.service_data.watermark : true)
  }
  
  // Validate if the user can edit the document or not and position it according to the corresponding step
  useEffect(() => {    
    if(oldService?.service_data && infoDataShareTo && infoDataShareTo[2] === false && inputValue && inputValue?.pos !== inputSign)
    {
      setInputValues(oldService.service_data.inputValues)
      oldService.service_data.inputValues.forEach(element => {
        if(inputValue.uid === element.uid)
        {
          setInputValue(element)          
        }
      });
    }
    else if(oldService?.service_data && infoDataShareTo && infoDataShareTo[2] === true && inputValue?.type === "sign" && inputValue?.pos !== inputSign)
    {
      const temp = oldService?.service_data?.inputValues.find((i: IInputValue) => i.uid === inputValue.uid)
      if(temp)
      {
        oldService.service_data.inputValues.forEach(element => {
          if(inputValue.uid === element.uid)
          {
            setInputValue(element)
            const posInput = inputValues.findIndex((i: IInputValue) => i.pos === element.pos)
            inputValues[posInput] = element
            setInputValues(inputValues)
          }
        });
      }
      else
      {
        inputValues.forEach(element => {
          if(inputValue.uid === element.uid)
          {
            setInputValue(undefined)
            const posInput = inputValues.findIndex((i: IInputValue) => i.uid === element.uid)
            inputValues.splice(posInput, 1)
            setInputValues(inputValues)
          }
        });
      }
    }
  }, [inputValues])

  // Load user data
  useEffect(() => {    
    setOldService(service)
    const loadUser = async () => {
      try {
        const _user = await getUser()
        if (_user) setUserObj(_user.data)
      } catch (error) {
        return error;
      }
    }
    loadUser()
  }, [])
  
  // Load userCreator data
  useEffect(() => {
    const loadUserCreator = async () => {
      try {
        const _user = await getUserId(service?.user_id)
        if (_user) setUserObjCreator(_user.data)
      } catch (error) {
        return error;
      }
    }
    loadUserCreator()
  }, [])

  // load data to user corresponding
  useEffect(() => {
    if (service && service.share_to)
    {
      service.share_to.forEach(element => {
        if(element[0] === userObj?.email)
        {
          setInfoDataShareTo(element)
        }   
      })
    }
  }, [userObj])

  // Validate if these are already loaded in order to render the document
  useEffect(() => {    
    if(inputValuesData && inputValuesData.length > 0 && templateState && templateState.id && watermark !== null )
    {
      setLoadedData(true)
    }
  }, [inputValuesData, templateState, watermark])

  // Set inputSign position where user can sign
  useEffect(() => {
    if(infoDataShareTo)
    {
      inputValues.forEach(element => {       
        if(element.uid === infoDataShareTo[1] && element.pos)
        {
          const posInput = findNextInputTypeSign(element.pos)
          setInputSign(posInput)
        } 
      })
    }
  }, [infoDataShareTo])

  // Define the position where the user will start editing
  useEffect(() => { 
    if(inputSign && infoDataShareTo && infoDataShareTo[2] === false)
    {
      setActiveStep(inputSign)
    }
    else if(inputSign && infoDataShareTo && infoDataShareTo[2] === true)
    {
      setActiveStep(0)
    }
  }, [inputSign])

  // Get the input according to the position
  useEffect(() => {
    if(activeStep)
    {
      const inputPos = inputValues.find((i: IInputValue) => i.pos === activeStep)
      setInputValue(inputPos)
    }
  }, [activeStep])

  // change watermark value
  useEffect(() => {
    if (service && watermark === false ) {
      onChangeService({
        service_data: { ...service.service_data, watermark: false },
      })
      service = {
        ...service,
        service_data: {
          ...service.service_data,
          watermark: false,
        } as IServiceData,
      }
    }
    if (service && watermark !== false ) {
      onChangeService({
        service_data: { ...service.service_data, watermark: true },
      })
      service = {
        ...service,
        service_data: {
          ...service.service_data,
          watermark: true,
        } as IServiceData,
      }
    }
  }, [watermark])

  // cahge firstDowload value
  useEffect(() => {
    if (service && !service?.service_data?.first_download && firstDowload) {
      onChangeService({
        service_data: { ...service.service_data, firstDowload: true },
      })
      service = {
        ...service,
        service_data: {
          ...service.service_data,
          first_download: true,
        } as IServiceData,
      }
    }
  }, [firstDowload])

  /**
   * Get inputSign position
   * @param {Number} posEmail
   * @return {Number} pos
  */
  const findNextInputTypeSign = (posEmail: number) => {
    let pos = posEmail;
    const currentIput = template.template_data?.inputs.findIndex((i: IInput, index: number) => index >= posEmail && i.type === "sign")
    if(currentIput) 
    { 
      pos = currentIput
    }
    return pos
  }
  
  /**
   * This method is used to find the next or previous input valid
   * @param {String} type
   * @Return {void}
  */
  const findNextInputConditional = (type: string) => {
    let nextInput
    if(type === 'add')
    {
      nextInput = templateData.inputs.findIndex((i: IInput, index: number) => index > activeStep && !i.conditional)
      if(nextInput && nextInput < 0)
      { 
        setActiveStep(templateData.inputs.length - 1)
      }
      else if(nextInput && nextInput >= 0)
      {
        validateConditionalStep('add', nextInput-1)
      }
    }
    else if(type === 'sub')
    {
      for (let index = 1; index < templateData.inputs.length; index++) {
        const element = templateData.inputs[activeStep - index];
        if(!element.conditional)
        {
          nextInput = element
          setActiveStep(activeStep - index)
          return
        }
      }
    }
  }  

  /**
   * This method is used to validate and find the next input according to the templateData.
   * @param {String} type
   * @param {Number} currentStep
   * @Return {void}
  */
  const validateConditionalStep = (
    type: 'add' | 'sub',
    currentStep: number
  ) => {
    setValidatorConditional(true)
    const step = currentStep + (type === 'add' ? 1 : -1)
    const inputEffect = templateData.inputs[step]

    if(!inputEffect)
    {
      if(step >= templateData.inputs.length) setValidatorConditional(false)
      findNextInputConditional(type)
      return
    }

    if (inputEffect.conditional) {
      // Improve multiple validation
      const cond: any = inputEffect.conditional
        .replace('{', '')
        .replace('}', '')
        .split('=')

      const inputValue = inputValues.find(
        (i: IInputValue) => i.pos === Number(cond[0]) - 1
      )
      if (inputValue) {
        let value = ''
        if (inputValue.type === 'select') {
          const options = inputValue.options?.split(',') || ''
          if (options && options.length > 0) {
            value = options[Number(inputValue.value)].trim()
          }
        } else value = inputValue.value.trim()
        if (value !== cond[1]) {
          //validar si hay un paso mas
          validateConditionalStep(type, step)
        } else {
          setActiveStep(step)
          const nextInput = templateData.inputs[step]
          const inputFind = inputValues.find(
            (i: IInputValue) => i.uid === nextInput.uid
          )
          if (inputFind) {
            setInputValue(inputFind)
          } else {
            setInputValue(undefined)
          }
        }
      } else {
        // console.error('INPUT MAL CONFIGURADO', inputEffect)
        validateConditionalStep(type, step)
      }
    } else {
      setActiveStep(step)
      const nextInput = templateData.inputs[step]
      const inputFind = inputValues.find(
        (i: IInputValue) => i.uid === nextInput.uid
      )
      if (inputFind) {
        setInputValue(inputFind)
      } else {
        setInputValue(undefined)
      }
    }
  }

  /**
   * This method is used to open newServiceDialog
   * @Return {void}
  */
  const openEndServiceDialog = () => {
    setOpenEndDialog(true)
  }

  /**
   * This method is used to add changes to array to send email
   * @Return {void}
  */
  const updateCurrentChanges = () =>
  {
    if(infoDataShareTo && infoDataShareTo[2] === true)
    {
      oldService?.service_data?.inputValues.forEach(element => {
        if(element.uid === inputValue?.uid)
        {
          if(element.value !== inputValue?.value || !oldService?.service_data?.inputValues.includes(inputValue))
          {
            const temp = [...currentChages]
            temp.push([inputValue.pos, element.value, inputValue?.value])
            setCurrentChages(temp)
          }
        }  
      })
    }
    if(infoDataShareTo && infoDataShareTo[2] === false && inputValue?.pos === inputSign)
    {
      oldService?.service_data?.inputValues.forEach(element => {
        if(element.pos === inputValue?.pos)
        {
          if(element.value !== inputValue?.value && userObj && userObjCreator)
          {
            sendSignDocumentEmail(userObj.email, userObjCreator.email)
          }
        }
      })
    }
  }

  /**
   * This method is used to save the value of the current step
   * validate the value of the input and current step
   * save the information depending on whether the service is new or already exists
   * @param {String} email
   * @param {String} uid
   * @Return {void}
  */
  const handleNext = async () => {
    if(inputValue?.value === undefined &&  templateData.inputs[activeStep].obligatory === true)
    {
      setAlertOptions({
        message: 'Este campo es obligatorio, por favor ingrese información válida para poder continuar',
        open: true,
        type: 'error',
      })
      return;
    }
    if (activeStep === 0)
    { 
      if(service && infoDataShareTo && infoDataShareTo[2] === true || 
        (service && infoDataShareTo && infoDataShareTo[2] === false && inputValue?.pos === inputSign))
      {
        const { error } = await updateService({
          service_id: service.id,
          ...service,
          service_data: {
            name: service?.service_data?.name,
            inputValues: inputValues,
            template_id: service?.service_data?.template_id,
            finished_document: oldService?.service_data?.finished_document,
            first_download: oldService?.service_data?.first_download,
            watermark: oldService?.service_data?.watermark,
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }
      validateConditionalStep('add', activeStep) 
    }
    else if (activeStep === templateData.inputs.length - 1)
    {
      if(service && infoDataShareTo && infoDataShareTo[2] === true ||
        (service && infoDataShareTo && infoDataShareTo[2] === false && inputValue?.pos === inputSign))
      {
        const { error } = await updateService({
          service_id: service.id,
          ...service,
          service_data: {
            name: service?.service_data?.name,
            inputValues: inputValues,
            template_id: service?.service_data?.template_id,
            finished_document: oldService?.service_data?.finished_document,
            first_download: oldService?.service_data?.first_download,
            watermark: oldService?.service_data?.watermark,
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }  
      openEndServiceDialog()       
    }
    else
    {
      if(service && infoDataShareTo && infoDataShareTo[2] === true || 
        (service && infoDataShareTo && infoDataShareTo[2] === false && inputValue?.pos === inputSign))
      {
        const { error } = await updateService({
          service_id: service.id,
          ...service,
          service_data: {
            name: service?.service_data?.name,
            inputValues: inputValues,
            template_id: service?.service_data?.template_id,
            finished_document: oldService?.service_data?.finished_document,
            first_download: oldService?.service_data?.first_download,
            watermark: oldService?.service_data?.watermark,
          } as IServiceData,
        })
        if (error) setAlertOptions({ message: error, open: true, type: 'error' }) 
      }
      validateConditionalStep('add', activeStep) 
    }             
    updateCurrentChanges() 
  }
  
  /**
   * This method is used to return to a previous valid input
   * @Return {void}
  */
  const handleBack = () => {
    setInputError({ error: false, label: '' })
    const inputFind = inputValues.find(
      (i: IInputValue) => i.uid === templateData.inputs[activeStep - 1].uid
    )
    if (inputFind) {
      setInputValue(inputFind)
    }
    validateConditionalStep('sub', activeStep)
    // setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  /**
   * This method is used to update the service
   * @Return {void}
  */
  const onChangeService = async (updatedFields: any, message?: string) => {
    if (service) {
      const { data, error } = await updateService({
        service_id: service.id,
        ...service,
        ...updatedFields,
      })
      if (data && message)
        setAlertOptions({
          message,
          open: true,
          type: 'info',
        })
      if (error) setAlertOptions({ message: error, open: true, type: 'error' })
    }
  }

  //
  /* const changeStateWatermark = () => {
    setWatermark(!watermark) 
  } */
  //

  return (
    <>    
      {user?.role === ROLES.SUPER_USER && (
        <SuperUserAppBar/>
      )}
      {user?.role === ROLES.USER && (
        <UserAppBar/>
      )} 
      <EndServiceDialog
        open={openEndDialog}
        template={template}
        setOpen={setOpenEndDialog}
        changes={currentChages}
        service={service}
      />
      <Alert alertOptions={alertOptions} setAlertOptions={setAlertOptions} />
      <DialogLink
        open={surveyDialogOpen}
        setOpen={setSurveyDialogOpen}
        title={'DANOS TU OPINION'}
        t1={'POR SUPUESTO'}
        t2={'POR AHORA NO'}
        description={
          '¡Tu opinión es muy importante para nosotros! Eres nuestra prioridad. \n Te agradecemos si puedes regalarnos unos minutos de tu tiempo para evaluar nuestros servicio.'
        }
        link={
          'https://docs.google.com/forms/d/e/1FAIpQLSf7WTq4AylN1C7J0ChJVetpNK1286YlCNtlPlDaa2ldLlrvaQ/viewform?usp=pp_url'
        }
      />
      <Container className={classes.container} maxWidth="xl">
      {loadedData && <BlobProvider document={TemplateGenerator(template, inputValues, watermark, service)}>
          {({ blob, error }) => {
            if (!error) {
              const reader = new FileReader()
              if (blob) {
                reader.readAsDataURL(blob)
                reader.onloadend = function () {
                  const base64data = reader.result
                  if (base64data) {
                    setBase64File(
                      base64data
                        .toString()
                        .replace('data:application/pdf;base64,', '')
                    )
                  }
                }
              }

              return <div></div>
            }
          }}
        </BlobProvider>}

        <Grid container alignContent={'center'} justifyContent={'center'} spacing={4}>
          <Grid item xs={12} sm={12} lg={6}>
            <Typography
              variant="subtitle1"
              align="center"
              color="primary"
              paragraph
              style={{
                marginBottom: '3vh',
                fontWeight: 'bold',
                fontSize: '1.4rem',
              }}
            >
              {service?.service_data
                ? service.service_data.name
                : template.name}
            </Typography>
            <Typography
              variant="subtitle2"
              align="justify"
              color="textSecondary"
              paragraph
              style={{ marginBottom: '25px', whiteSpace: 'pre-line' }}
            >
              {template.description}
            </Typography>

            {((inputValue?.value === undefined && templateData.inputs[activeStep].obligatory === true) &&
              <>
                <Typography
                  variant="subtitle2"
                  align="justify"
                  color="textSecondary"
                  paragraph
                  style={{ marginLeft: '2.2vh', whiteSpace: 'pre-line', marginBottom: '5px' }}
                >
                  Campo obligatorio*
                </Typography>
              </>            
            )}

            {( service /*&& infoDataShareTo  && infoDataShareTo[2] === true  */&&
              <>
                <MobileStepper
                  variant="progress"
                  steps={templateData.inputs.length}
                  position="static"
                  activeStep={activeStep}
                  className={classes.stepper}
                  nextButton={
                    <Button
                      size="small"
                      onClick={handleNext}
                      disabled={inputError.error}
                    >
                      {activeStep === templateData.inputs.length - 1
                        ? 'FINALIZAR'
                        : 'SIGUIENTE'}
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowLeft />
                      ) : (
                        <KeyboardArrowRight />
                      )}
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      onClick={handleBack}
                      disabled={activeStep === 0}
                    >
                      {theme.direction === 'rtl' ? (
                        <KeyboardArrowRight />
                      ) : (
                        <KeyboardArrowLeft />
                      )}
                      ATRÁS
                    </Button>
                  }
                />
                {validatorConditional === true && activeStep <= templateData.inputs.length - 1 && (
                  <InputItem
                    inputError={inputError}
                    setInputError={setInputError}
                    inputsConfig={{ inputValue, setInputValue }}
                    pos={activeStep}
                    input={templateData.inputs[activeStep]}
                    inputValues={inputValues}
                    setInputValues={setInputValues}
                  />
                )}
              </>
            )}
            {activeStep >= templateData.inputs.length - 1 && validatorConditional === false && (
              <>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  color="textPrimary"
                  gutterBottom
                  style={{ fontSize: '0.9rem', fontWeight: 'normal', marginTop: '1.3rem' }}
                >
                  Ya no tienes que diligenciar más información, por favor presiona finalizar para culminar la creación de tu documento.
                </Typography>
              </>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            lg={6}
            style={{
              display: 'flex',
              justifyContent: 'center',
              maxHeight: '100vh',
              marginBottom: '50vh'
            }}
          >
            {base64File && (
              <FilePreviewer
                hideDownload={
                  service && 
                  service.service_status_id === SERVICE_STATUS.PAID && 
                  service.service_data?.finished_document === true
                    ? false
                    : true
                }/*  
                // 
                changeStateWatermark={changeStateWatermark} */
                //
                firstDowload={firstDowload}
                setSurveyDialogOpen={setSurveyDialogOpen}
                setFirstDowload={setFirstDowload}
                file={{
                  data: base64File,
                  mimeType: 'application/pdf',
                  name: `${service?.service_data?.name}.pdf`, // for download
                }}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default TemplateEditor